import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import { CbridgePalette } from '@cbridge-utils/mui-theme';
import { getLanguageName } from './_helpers/getLanguageName';
import { ILanguageSwitcherProps } from './LanguageSwitcher.interface';

// A toolbar control, not a form field: no primary-coloured focus ring, the focused border looks like the hovered one.
const LanguageSelect = styled(Select<string>)({
  minWidth: 120,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: CbridgePalette.border,
  },
  '&:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: CbridgePalette.text.disabled,
    borderWidth: 1,
  },
});

/**
 * Language picker for the app toolbar; a single language is still shown, only an empty list renders nothing.
 * No tooltip on purpose: the native language name is self-explanatory, and the select keeps focus
 * (and often the pointer) after a choice, which kept a tooltip open over the options and after closing.
 */
export const LanguageSwitcher: FunctionComponent<ILanguageSwitcherProps> = (props) => {
  const { languages, value, onChange, fullWidth = false } = props;
  const { t } = useTranslation();

  if (languages.length === 0) {
    return null;
  }

  const selected = languages.includes(value) ? value : '';

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  return (
    <LanguageSelect
      size={'small'}
      fullWidth={fullWidth}
      value={selected}
      onChange={handleChange}
      inputProps={{ 'aria-label': t('text:common.field.language.label') }}
    >
      {languages.map((language) => (
        <MenuItem key={language} value={language}>
          {getLanguageName(language)}
        </MenuItem>
      ))}
    </LanguageSelect>
  );
};
